import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// types
import { GratificationPopupLottieData, PostStateData } from "@super-ui/components/m2c-swiggy-one-halfcard/types";
import { LOTTIE_ERROR_TYPES } from "@swiggy-private/core-types";

// styles
import {
    HeaderContainer,
    HeaderText,
    InfoContainer,
    Logo,
    LogoContainer,
    Progress,
    ProgressBarContainer,
    SparklingStars,
    InfoContent,
    SubTitlesContainer,
    CtaContainer,
    ProgressBarWrapper,
    LottieContainer,
} from "./styles";

// constants
import {
    M2C_HALFCARD_TEST_IDS,
    DEFAULT_PROGRESS_DURATION,
    SPARKLING_STARS,
    DISABLE_GREY_FROM_IMG,
    THOUSAND,
    SWIGGY_ONE_LITE_ADDED_TEXT,
    SWIGGY_ONE_ADDED_TEXT,
    GRATIFICATION_POPUP_LOTTIE_ASSETS,
    GRATIFICATION_POPUP_LOTTIE_SELECTORS,
    BILL_SUBTEXT_ONE,
    BILL_SUBTEXT_ONE_LITE,
} from "@super-ui/components/m2c-swiggy-one-halfcard/constants";
import { GRATIFICATION_CLOSE_BUTTON } from "@super-ui/components/swiggy-one-widget-dls-wrapper/constants";

// utils
import { format, isEmpty } from "@swiggy-private/js-utils";
import { replaceAttributes } from "@super-ui/components/m2c-swiggy-one-halfcard/helpers";
import { LottiePlayerComp } from "./components/LottiePlayer";
import { NEWRELIC_TABLES, recordNREvent } from "@swiggy-private/checkout-core";
import { captureError } from "@swiggy-private/core-ui";

interface PostStateLayoutProps extends PostStateData {
    isOneLite?: boolean;
    isPopup?: boolean;
    showLottieExp?: boolean;
}

const PostStateLayout: FC<PostStateLayoutProps> = ({
    duration = DEFAULT_PROGRESS_DURATION,
    headerText,
    logoCtx,
    title,
    isOneLite,
    closeBottomSheet,
    cta,
    subTitles,
    attributeValues,
    isPopup = false,
    showLottieExp,
}) => {
    const ctaText = useMemo(() => cta?.text, [cta]);
    // [!IMPORTANT] Deriving state from props as an antipattern here
    // since we need to handle lottie fail scenarios as well.
    // Owner of this state will be PostStateLayout.tsx since
    // this has to decide between showing lottie/non-lottie exp.
    const [showLottie, setShowLottie] = useState(showLottieExp);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const formattedHeader = useMemo(() => replaceAttributes(headerText, attributeValues), [
        attributeValues,
        headerText,
    ]);

    const formattedTitle = useMemo(() => replaceAttributes(title, attributeValues), [attributeValues, title]);

    const gratificationPopupLottieData = useMemo(
        () =>
            ({
                previousAmount: attributeValues?.cart_total_plan_not_added,
                updatedAmount: attributeValues?.cart_total_plan_added,
                heading: isOneLite ? SWIGGY_ONE_LITE_ADDED_TEXT : SWIGGY_ONE_ADDED_TEXT,
                logo: isOneLite
                    ? GRATIFICATION_POPUP_LOTTIE_ASSETS.ONE_LITE_LOGO
                    : GRATIFICATION_POPUP_LOTTIE_ASSETS.ONE_LOGO,
                billSubtext: isOneLite
                    ? format(BILL_SUBTEXT_ONE_LITE, `₹${attributeValues?.plan_in_cart_savings}`)
                    : format(BILL_SUBTEXT_ONE, `₹${attributeValues?.plan_in_cart_savings}`),
            } as GratificationPopupLottieData),
        [attributeValues, isOneLite],
    );

    const formattedSubTitles = useCallback((subTitle: string) => replaceAttributes(subTitle, attributeValues), [
        attributeValues,
    ]);

    const onClose = useCallback(() => closeBottomSheet?.(GRATIFICATION_CLOSE_BUTTON.CTA_CLICK), [closeBottomSheet]);

    // will run after lottie is loaded
    const lottieCloseClickHandler = useCallback(() => {
        try {
            const lottieSVGLayer = document.querySelector(GRATIFICATION_POPUP_LOTTIE_SELECTORS.SVG_SELECTOR);
            const yayTextLayer = lottieSVGLayer?.querySelector(
                GRATIFICATION_POPUP_LOTTIE_SELECTORS.YAY_LAYER_SELECTOR,
            ) as HTMLElement;
            yayTextLayer.style.cursor = "pointer";
            yayTextLayer.addEventListener("click", () => {
                onClose();
            });
        } catch (error) {
            // just capturing error; lottie will close automatically once the timer runs out
            captureError(error);
        }
    }, [onClose]);

    const onLottieError = useCallback((errorType?: LOTTIE_ERROR_TYPES, error?: unknown) => {
        setShowLottie(false);
        recordNREvent({
            eventType: NEWRELIC_TABLES.ONE_GRATIFICATION_POPUP_EVENTS,
            errorType: String(errorType),
            error: String(error),
        });
    }, []);

    useEffect(() => {
        timerRef.current = setTimeout(() => {
            closeBottomSheet?.(GRATIFICATION_CLOSE_BUTTON.AUTO_COLLAPSE);
        }, duration * THOUSAND);

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [headerText, logoCtx, title, isOneLite, duration, closeBottomSheet]);

    const renderNonLottieContent = useCallback(
        () => (
            <InfoContainer isPopup={isPopup}>
                <InfoContent isPopup={isPopup}>
                    <LogoContainer>
                        <Logo isOneLite={isOneLite}>
                            <Image
                                cloudinaryId={logoCtx.logo}
                                alt={logoCtx.text}
                                customBackground={DISABLE_GREY_FROM_IMG}
                            />
                        </Logo>
                    </LogoContainer>

                    <Text
                        data-testid={M2C_HALFCARD_TEST_IDS.GRATIFICATION_TITLE}
                        variant="V2.heading1.neutral.black"
                        color="text_Highest_Emphasis"
                        dangerouslySetInnerHTML={{ __html: formattedTitle }}
                    />

                    {!isEmpty(subTitles) && (
                        <SubTitlesContainer>
                            {subTitles.length > 0 &&
                                subTitles.map((subTitle, index) => (
                                    <Text
                                        key={index}
                                        variant="V2.body2.neutral.regular"
                                        color="text_Low_Emphasis"
                                        dangerouslySetInnerHTML={{ __html: formattedSubTitles(subTitle) }}
                                    />
                                ))}
                        </SubTitlesContainer>
                    )}

                    {!isEmpty(ctaText) && (
                        <CtaContainer>
                            <Text variant="V2.subheading1.neutral.medium" color="primary" onClick={onClose}>
                                {ctaText}
                            </Text>
                        </CtaContainer>
                    )}
                </InfoContent>
            </InfoContainer>
        ),
        [
            ctaText,
            formattedSubTitles,
            formattedTitle,
            isOneLite,
            isPopup,
            logoCtx.logo,
            logoCtx.text,
            onClose,
            subTitles,
        ],
    );

    return (
        <>
            {showLottie && (
                <LottieContainer>
                    <LottiePlayerComp
                        onError={onLottieError}
                        gratificationPopUpData={gratificationPopupLottieData}
                        isGratificationPopup
                        domLoadedCallbacks={[lottieCloseClickHandler]}
                    />
                </LottieContainer>
            )}
            {!isEmpty(headerText) && isEmpty(showLottie) && (
                <HeaderContainer data-testid={M2C_HALFCARD_TEST_IDS.GRATIFICATION_HEADER} isPopup={isPopup}>
                    <SparklingStars isPopup={isPopup}>
                        <Image
                            cloudinaryId={SPARKLING_STARS}
                            cloudinaryHeight={100}
                            customBackground={DISABLE_GREY_FROM_IMG}
                        />
                    </SparklingStars>
                    <HeaderText
                        isPopup={isPopup}
                        variant={isPopup ? "V2.body2.neutral.bold" : "V2.body1.neutral.bold"}
                        color="positive"
                        dangerouslySetInnerHTML={{ __html: formattedHeader }}
                    />
                    <SparklingStars isPopup={isPopup} leftPlaced={false}>
                        <Image
                            cloudinaryId={SPARKLING_STARS}
                            cloudinaryHeight={100}
                            customBackground={DISABLE_GREY_FROM_IMG}
                        />
                    </SparklingStars>
                </HeaderContainer>
            )}

            {isEmpty(showLottie) && renderNonLottieContent()}

            <ProgressBarWrapper withLottie={showLottie} isPopup={isPopup}>
                <ProgressBarContainer isPopup={isPopup}>
                    <Progress duration={duration} isPopup={isPopup} />
                </ProgressBarContainer>
            </ProgressBarWrapper>
        </>
    );
};

export default memo(PostStateLayout);
